import travelClient from './travelClient';
import { travelEndpoints } from './endpoints';

/**
 * Preserve Travel snowflake IDs that exceed Number.MAX_SAFE_INTEGER.
 * Backend JSON may emit journeyId as a bare number; JS Number would corrupt it.
 */
function parseTravelJson(raw) {
  if (typeof raw !== 'string' || !raw) return raw;
  const text = raw.replace(
    /("(?:journeyId|journeyDayId|dayId|activityId|itineraryId|ownerMemberId|creatorMemberId|memberId|invitationId|recipientMemberId|inviterMemberId|previousOwnerMemberId|currentOwnerMemberId|removalActorMemberId|targetMemberId|sourceId|timelineItemId|conversationId|bindingId)"\s*:\s*)(\d{15,})/g,
    '$1"$2"',
  );
  return JSON.parse(text);
}

const snowflakeJson = { transformResponse: [parseTravelJson] };

function lifecycleBody(expectedRowVersion, extra = {}) {
  return {
    expectedRowVersion,
    confirm: true,
    ...extra,
  };
}

/** AUTH58：leave／remove 必須由呼叫端明確傳 confirm，避免 API 層靜默代填。 */
function membershipMutationBody({ expectedRowVersion, confirm, reason } = {}) {
  if (expectedRowVersion == null || expectedRowVersion === '') {
    throw new Error('expectedRowVersion is required');
  }
  if (confirm !== true) {
    throw new Error('confirm must be true');
  }
  const body = {
    expectedRowVersion,
    confirm: true,
  };
  if (reason != null && String(reason).trim() !== '') {
    body.reason = String(reason).trim();
  }
  return body;
}

function ownershipTransferBody({ targetMemberId, expectedRowVersion } = {}) {
  if (targetMemberId == null || targetMemberId === '') {
    throw new Error('targetMemberId is required');
  }
  if (expectedRowVersion == null || expectedRowVersion === '') {
    throw new Error('expectedRowVersion is required');
  }
  return {
    targetMemberId,
    expectedRowVersion,
  };
}

function invitationInboxConfig(cursor = {}) {
  const params = {};
  if (cursor.limit != null) params.limit = cursor.limit;
  if (cursor.status) params.status = cursor.status;
  if (cursor.cursorInvitedAt && cursor.cursorInvitationId) {
    params.cursorInvitedAt = cursor.cursorInvitedAt;
    params.cursorInvitationId = cursor.cursorInvitationId;
  }
  return { ...snowflakeJson, params };
}

/** Private member Journey API — ownership-separated from public publication reads. */
export const journeyApi = {
  listMine: () => travelClient.get(travelEndpoints.journeys, snowflakeJson),
  create: (body) => travelClient.post(travelEndpoints.journeys, body, snowflakeJson),
  getDetail: (journeyId) => travelClient.get(travelEndpoints.journey(journeyId), snowflakeJson),
  updateBasicInformation: (journeyId, body) => travelClient.patch(
    travelEndpoints.journeyBasicInformation(journeyId),
    body,
    snowflakeJson,
  ),
  markReady: (journeyId, { expectedRowVersion }) => travelClient.post(
    travelEndpoints.journeyReady(journeyId),
    lifecycleBody(expectedRowVersion),
    snowflakeJson,
  ),
  start: (journeyId, { expectedRowVersion }) => travelClient.post(
    travelEndpoints.journeyStart(journeyId),
    lifecycleBody(expectedRowVersion),
    snowflakeJson,
  ),
  complete: (journeyId, { expectedRowVersion }) => travelClient.post(
    travelEndpoints.journeyComplete(journeyId),
    lifecycleBody(expectedRowVersion),
    snowflakeJson,
  ),
  cancel: (journeyId, { expectedRowVersion, reason = null }) => travelClient.post(
    travelEndpoints.journeyCancel(journeyId),
    lifecycleBody(expectedRowVersion, reason == null || reason === '' ? {} : { reason }),
    snowflakeJson,
  ),
  archive: (journeyId, { expectedRowVersion }) => travelClient.post(
    travelEndpoints.journeyArchive(journeyId),
    lifecycleBody(expectedRowVersion),
    snowflakeJson,
  ),
  addDay: (journeyId, body) => travelClient.post(
    travelEndpoints.journeyDays(journeyId),
    body,
    snowflakeJson,
  ),
  updateDay: (journeyId, journeyDayId, body) => travelClient.patch(
    travelEndpoints.journeyDay(journeyId, journeyDayId),
    body,
    snowflakeJson,
  ),
  removeDay: (journeyId, journeyDayId, { expectedRowVersion, confirm = true }) => travelClient.delete(
    travelEndpoints.journeyDay(journeyId, journeyDayId),
    { params: { expectedRowVersion, confirm }, ...snowflakeJson },
  ),
  moveDay: (journeyId, journeyDayId, body) => travelClient.post(
    travelEndpoints.journeyDayMove(journeyId, journeyDayId),
    body,
    snowflakeJson,
  ),
  createInvitation: (journeyId, body) => travelClient.post(
    travelEndpoints.journeyInvitations(journeyId),
    body,
    snowflakeJson,
  ),
  listIncomingInvitations: (cursor) => travelClient.get(
    travelEndpoints.incomingJourneyInvitations,
    invitationInboxConfig(cursor),
  ),
  getIncomingInvitation: (invitationId) => travelClient.get(
    travelEndpoints.incomingJourneyInvitation(invitationId),
    snowflakeJson,
  ),
  acceptInvitation: (invitationId) => travelClient.post(
    travelEndpoints.journeyInvitationAccept(invitationId),
    {},
    snowflakeJson,
  ),
  declineInvitation: (invitationId) => travelClient.post(
    travelEndpoints.journeyInvitationDecline(invitationId),
    {},
    snowflakeJson,
  ),
  revokeInvitation: (invitationId) => travelClient.post(
    travelEndpoints.journeyInvitationRevoke(invitationId),
    {},
    snowflakeJson,
  ),
  /** AUTH58：ACTIVE 非 Owner 離開旅程（ACTIVE → LEFT）。 */
  leave: (journeyId, { expectedRowVersion, confirm } = {}) => travelClient.post(
    travelEndpoints.journeyLeave(journeyId),
    membershipMutationBody({ expectedRowVersion, confirm }),
    snowflakeJson,
  ),
  /** AUTH58：移除 ACTIVE 非 Owner 成員（ACTIVE → REMOVED）。FE 不得送 self-remove。 */
  removeMember: (journeyId, memberId, { expectedRowVersion, confirm, reason } = {}) => travelClient.post(
    travelEndpoints.journeyMemberRemove(journeyId, memberId),
    membershipMutationBody({ expectedRowVersion, confirm, reason }),
    snowflakeJson,
  ),
  /** AUTH58：Owner 轉移所有權；舊 Owner 成為 COORDINATOR。 */
  transferOwnership: (journeyId, { targetMemberId, expectedRowVersion } = {}) => travelClient.post(
    travelEndpoints.journeyOwnershipTransfer(journeyId),
    ownershipTransferBody({ targetMemberId, expectedRowVersion }),
    snowflakeJson,
  ),
};
